// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { authService } from '../authService';
import { AUTH_CONFIG } from '../config';

// JWT token decoder function
const decodeJwtToken = (token) => {
  if (!token) {
    console.warn("No token provided to decode");
    return null;
  }
  
  try {
    // Split the token
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error("Invalid JWT format");
      return null;
    }
    
    // Decode the payload (middle part)
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload;
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
};

// Create the auth context
const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const tokenRefreshTimeout = useRef(null);
  // Add a counter to prevent refresh loops
  const refreshAttempts = useRef(0);
  const maxRefreshAttempts = 3;
  const refreshCooldown = useRef(false);
  
  // Get user's timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  // Utility to safely parse an expiry timestamp - fixed timezone handling
  const parseExpiryTime = (expiresAt) => {
    if (!expiresAt) {
      console.warn("No expiry time provided");
      return null;
    }
    
    // Log what we received for debugging
    console.log("Parsing expiry time:", expiresAt, "type:", typeof expiresAt);
    
    let timestamp;
    
    // Case 1: Already a number
    if (typeof expiresAt === 'number') {
      timestamp = expiresAt;
      console.log("Numeric input:", timestamp);
    }
    // Case 2: String that needs parsing
    else if (typeof expiresAt === 'string') {
      // Try as direct number first
      const asNumber = parseInt(expiresAt, 10);
      if (!isNaN(asNumber)) {
        timestamp = asNumber;
        console.log("String numeric parsed as:", timestamp);
      } else {
        // Try as date string
        timestamp = Date.parse(expiresAt);
        console.log("Date string parsed as:", timestamp);
        
        if (isNaN(timestamp)) {
          console.error("Failed to parse as number or date:", expiresAt);
          return null;
        }
      }
    } else {
      console.error("Unsupported input type:", typeof expiresAt);
      return null;
    }
    
    if (isNaN(timestamp)) {
      console.error("Timestamp is NaN after parsing:", expiresAt);
      return null;
    }
    
    // Ensure timestamp is in milliseconds (standard for JavaScript)
    // JWT standard uses seconds, so convert if it appears to be in seconds
    
    // If less than year 2000 in milliseconds, likely in seconds
    if (timestamp < 946684800000) { // Jan 1, 2000
      console.log("Timestamp appears to be in seconds, converting to milliseconds:", timestamp);
      timestamp *= 1000;
    }
    
    const now = Date.now();
    
    // Get dates in UTC for consistent comparison
    const expiryDate = new Date(timestamp);
    const nowDate = new Date(now);
    
    // Format both dates in UTC for logging
    const expiryUTC = expiryDate.toISOString();
    const nowUTC = nowDate.toISOString();
    
    console.log(`Expiry date (UTC): ${expiryUTC}`);
    console.log(`Current date (UTC): ${nowUTC}`);
    
    // Calculate time difference in minutes for better debugging
    const diffMinutes = Math.round((timestamp - now) / (60 * 1000));
    console.log(`Time difference: ${diffMinutes} minutes`);
    
    // Account for small time differences - consider tokens within 5 minutes of expiry as valid
    // but needing immediate refresh. This helps with timezone differences and clock skew.
    if (diffMinutes < -5) {
      console.warn("WARNING: Timestamp is clearly in the past by more than 5 minutes");
    } else if (diffMinutes < 5) {
      console.warn("Timestamp is very close to current time (within 5 minutes)");
      // We'll still return it but flag it as needing refresh
    } else {
      console.log(`Timestamp is valid and expires in approximately ${diffMinutes} minutes`);
    }
    
    return timestamp;
  };

  // Setup token refresh with JWT expiry parsing
  const setupTokenRefresh = (expiresAt) => {
    // Clear any existing refresh timeout
    if (tokenRefreshTimeout.current) {
      clearTimeout(tokenRefreshTimeout.current);
      tokenRefreshTimeout.current = null;
    }
    
    // Reset refresh attempts when setting up a new refresh
    refreshAttempts.current = 0;
    
    // First try to get expiry from JWT directly
    let tokenExpiry = null;
    const token = authService.getToken();
    
    if (token) {
      const decodedToken = decodeJwtToken(token);
      if (decodedToken && decodedToken.exp) {
        tokenExpiry = decodedToken.exp * 1000; // Convert seconds to milliseconds
        console.log("Using JWT expiry for refresh scheduling:", new Date(tokenExpiry).toISOString());
      }
    }
    
    // If we couldn't get JWT expiry, use provided expiresAt
    const expiry = tokenExpiry || parseExpiryTime(expiresAt);
    if (!expiry) {
      console.log("Invalid or missing expiration time, not scheduling refresh");
      return;
    }
    
    const currentTime = Date.now();
    const diffMs = expiry - currentTime;
    const diffMinutes = Math.round(diffMs / (60 * 1000));
    
    console.log(`Setting up token refresh. Expiry: ${new Date(expiry).toISOString()}`);
    console.log(`Current time: ${new Date(currentTime).toISOString()}`);
    console.log(`Time difference: ${diffMinutes} minutes`);
    
    // If token is already expired by more than 5 minutes, don't schedule refresh
    if (diffMinutes < -5) {
      console.log("Token already expired by more than 5 minutes, not scheduling refresh");
      return;
    }
    
    // If token expires very soon (within 5 minutes) or appears to be in the past but within tolerance,
    // schedule a refresh with a minimum delay to prevent immediate back-to-back refreshes
    if (diffMinutes < 5) {
      const safeDelay = Math.max(5000, 2000 * (diffMinutes + 3)); // At least 5 seconds, or proportional
      console.log(`Token expires very soon or has timestamp near current time. Refreshing in ${Math.round(safeDelay / 1000)} seconds`);
      
      tokenRefreshTimeout.current = setTimeout(async () => {
        console.log("Executing soon-expiring token refresh");
        await refreshToken();
      }, safeDelay);
      return;
    }
    
    // For normal cases, refresh 5 minutes before expiry
    const timeUntilRefresh = Math.max(0, diffMs - (5 * 60 * 1000));
    console.log(`Token refresh scheduled in ${Math.round(timeUntilRefresh / 1000 / 60)} minutes`);
    
    tokenRefreshTimeout.current = setTimeout(async () => {
      console.log("Refreshing token automatically");
      await refreshToken();
    }, timeUntilRefresh);
  };

  // Improved refreshToken function using JWT expiry and timezone info
  const refreshToken = async () => {
    // Prevent refresh attempts if in cooldown
    if (refreshCooldown.current) {
      console.log("Token refresh on cooldown, skipping");
      return null;
    }
    
    // Prevent refresh loops
    if (refreshAttempts.current >= maxRefreshAttempts) {
      console.error(`Maximum refresh attempts (${maxRefreshAttempts}) reached. Signing out.`);
      refreshCooldown.current = true;
      
      // Wait a bit before allowing refreshes again
      setTimeout(() => {
        refreshCooldown.current = false;
        refreshAttempts.current = 0;
      }, 60000); // 1 minute cooldown
      
      await signOut();
      return null;
    }
    
    refreshAttempts.current += 1;
    
    try {
      const refresh_token = authService.getRefreshToken();
      if (!refresh_token) {
        console.log("No refresh token available");
        return null;
      }
      
      console.log("Refreshing access token");
      const response = await fetch(`${AUTH_CONFIG.apiUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh_token })
      });
      
      if (!response.ok) {
        console.error("Token refresh failed:", response.status);
        
        // If bad request or unauthorized, clear tokens and sign out
        if (response.status === 400 || response.status === 401) {
          await signOut();
        }
        return null;
      }
      
      // Get the response as text first for debugging
      const responseText = await response.text();
      console.log("Token refresh response:", responseText);
      
      // Parse the response text into JSON
      let tokens;
      try {
        tokens = JSON.parse(responseText);
      } catch (e) {
        console.error("Failed to parse refresh response as JSON:", e);
        return null;
      }
      
      // Log token response for debugging
      console.log("Token response structure:", Object.keys(tokens));
      
      // Validate received tokens
      if (!tokens || !tokens.access_token) {
        console.error("Invalid token response received");
        return null;
      }
      
      // Save new tokens
      authService.saveTokens(tokens);
      
      // Reset refresh attempts counter on successful refresh
      refreshAttempts.current = 0;
      
      // Update user state from token response
      setUser({
        id: tokens.user_id,
        email: tokens.email
      });
      
      // Update profile if it's included in the response
      if (tokens.profile) {
        setProfile(tokens.profile);
      }
      
      // Get timezone info from the token if available
      const decodedToken = decodeJwtToken(tokens.access_token);
      //console.log("Decoded JWT token:", decodedToken);
      
      // JWT expiry is in seconds, convert to milliseconds
      let jwtExpiry = null;
      if (decodedToken && decodedToken.exp) {
        jwtExpiry = decodedToken.exp * 1000; // Convert seconds to milliseconds
        console.log("JWT expiry time:", new Date(jwtExpiry).toISOString());
      }
      
      // Check for timezone info in token
      let tokenTimezone = null;
      if (decodedToken && decodedToken.tz) {
        tokenTimezone = decodedToken.tz;
        console.log("Token contains timezone:", tokenTimezone);
      }
      
      // Use JWT expiry if available, fall back to expires_at with safer parsing
      let expiryToUse = jwtExpiry;
      if (!expiryToUse) {
        console.log("JWT expiry not found, falling back to expires_at");
        expiryToUse = parseExpiryTime(tokens.expires_at);
      }
      
      if (expiryToUse) {
        const now = Date.now();
        const diffMs = expiryToUse - now;
        const diffMinutes = Math.round(diffMs / (60 * 1000));
        
        console.log(`Using expiry time: ${new Date(expiryToUse).toISOString()}`);
        console.log(`Current time: ${new Date(now).toISOString()}`);
        console.log(`Time until expiry: ${diffMinutes} minutes`);
        
        // Schedule refresh if token is valid or within a reasonable timeframe
        if (diffMinutes > -5) {
          setupTokenRefresh(expiryToUse);
        } else {
          console.warn("Token appears to have already expired by more than 5 minutes");
          // Still return the token since the API accepted it
        }
      } else {
        console.warn("Could not determine token expiry time");
      }
      
      return tokens;
    } catch (error) {
      console.error("Error refreshing token:", error);
      return null;
    }
  };

  // Function to manually refresh the auth state
  const refreshAuthState = async () => {
    console.log("Manually refreshing auth state");
    setLoading(true);
    
    try {
      // Check local storage for tokens
      const tokens = authService.loadTokens();
      
      if (tokens && tokens.access_token) {
        // Try to refresh the token
        await refreshToken();
      } else {
        // Clear any stale data
        setUser(null);
        setProfile(null);
      }
    } catch (error) {
      console.error("Error refreshing auth state:", error);
      // Clear user state on error
      setUser(null);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  // Auth API functions
  const signIn = async (email, password) => {
    try {
      const response = await fetch(`${AUTH_CONFIG.apiUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData.detail || 'Login failed' };
      }

      const tokenData = await response.json();
      
      // Validate token data
      if (!tokenData || !tokenData.access_token) {
        return { error: 'Invalid token data received' };
      }
      
      // Save tokens using our authService
      authService.saveTokens(tokenData);
      
      // Reset refresh attempts
      refreshAttempts.current = 0;
      
      // Update user state
      setUser({
        id: tokenData.user_id,
        email: tokenData.email
      });
      
      // Update profile if available
      if (tokenData.profile) {
        setProfile(tokenData.profile);
      }
      
      // Setup token refresh
      const expiryTime = parseExpiryTime(tokenData.expires_at);
      if (expiryTime) {
        setupTokenRefresh(expiryTime);
      }
      
      return { data: tokenData };
    } catch (error) {
      console.error('Login error:', error);
      return { error: 'Authentication failed' };
    }
  };

  const requestEmailVerification = async (email) => {
    try {
      const response = await fetch(`${AUTH_CONFIG.apiUrl}/auth/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData.detail || 'Failed to send verification email' };
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Email verification request error:', error);
      return { error: 'Failed to send verification email' };
    }
  };

// Updated verifyEmail function in AuthContext.js
const verifyEmail = async (email, otpCode) => {
  try {
    const response = await fetch(`${AUTH_CONFIG.apiUrl}/auth/verify-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ 
        email, 
        otp_code: otpCode,
        password_bind: true // Add the password_bind flag
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      return { error: errorData.detail || 'Email verification failed' };
    }

    const tokenData = await response.json();
    
    // Validate token data
    if (!tokenData || !tokenData.access_token) {
      return { error: 'Invalid token data received' };
    }
    
    // Check if password setup is necessary
    const necessaryPasswordRegistration = tokenData.necessary_password_registration || false;
    
    // Save tokens using our authService
    authService.saveTokens(tokenData);
    
    // Reset refresh attempts
    refreshAttempts.current = 0;
    
    // Update user state
    setUser({
      id: tokenData.user_id,
      email: tokenData.email
    });
    
    // Update profile if available
    if (tokenData.profile) {
      setProfile(tokenData.profile);
    }
    
    // Setup token refresh
    const expiryTime = parseExpiryTime(tokenData.expires_at);
    if (expiryTime) {
      setupTokenRefresh(expiryTime);
    }
    
    return { 
      data: tokenData,
      necessaryPasswordRegistration
    };
  } catch (error) {
    console.error('Email verification error:', error);
    return { error: 'Verification failed' };
  }
};

  const checkEmailExists = async (email) => {
    try {
      const token = authService.getToken();
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${AUTH_CONFIG.apiUrl}/auth/email-exist?email=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData.detail || 'Failed to check email' };
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Check email error:', error);
      return { error: 'Failed to check if email exists' };
    }
  };

  const updateUserProfile = async (updates) => {
    try {
      const token = authService.getToken();
      if (!token) {
        return { error: 'Not authenticated' };
      }

      const response = await fetch(`${AUTH_CONFIG.apiUrl}/auth/user`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData.detail || 'Failed to update profile' };
      }

      const data = await response.json();
      
      // Update local profile state if the update was successful
      if (data.success && data.data?.profile) {
        setProfile(data.data.profile);
      }
      
      return { data };
    } catch (error) {
      console.error('Profile update error:', error);
      return { error: 'Failed to update profile' };
    }
  };

  // Improved signOut function
  const signOut = async () => {
    try {
      console.log("Signing out user");
      
      // Clear token refresh timeout
      if (tokenRefreshTimeout.current) {
        clearTimeout(tokenRefreshTimeout.current);
        tokenRefreshTimeout.current = null;
      }
      
      // Try to call logout API
      const token = authService.getToken();
      if (token) {
        try {
          await fetch(`${AUTH_CONFIG.apiUrl}/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        } catch (error) {
          console.error('Logout API error:', error);
          // Continue with local logout
        }
      }
      
      // Clear tokens - using authService directly
      authService.removeToken();
      
      // Clear user state
      setUser(null);
      setProfile(null);
      
      // Reset refresh counter
      refreshAttempts.current = 0;
      
      return { success: true };
    } catch (error) {
      console.error('Sign out error:', error);
      
      // Force token clearing even on error
      authService.removeToken();
      setUser(null);
      setProfile(null);
      refreshAttempts.current = 0;
      
      return { success: false, error: 'Failed to sign out properly' };
    }
  };

  // Initialize auth state from stored tokens with JWT expiry parsing
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true);
        
        // Check if we have tokens in storage using authService
        const tokens = authService.loadTokens();
        console.log('Loaded tokens from storage:', tokens ? 'found' : 'not found');
        
        if (!tokens || !tokens.access_token) {
          console.log('No stored tokens found');
          
          // Also check legacy token
          if (authService.isAuthenticated()) {
            console.log('Found legacy token, trying to use it');
            const legacyToken = localStorage.getItem(AUTH_CONFIG.tokenKey);
            
            // Try to get user info with legacy token
            try {
              const response = await fetch(`${AUTH_CONFIG.apiUrl}/auth/verify`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${legacyToken}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (response.ok) {
                const userData = await response.json();
                setUser({
                  id: userData.data?.user_id,
                  email: userData.data?.email || "unknown"
                });
              } else {
                // Clear legacy token if invalid
                authService.removeToken();
              }
            } catch (e) {
              console.error('Error with legacy token:', e);
              authService.removeToken();
            }
          }
          
          setLoading(false);
          setInitialized(true);
          return;
        }
        
        // IMPORTANT: Get expiry directly from JWT token instead of relying on expires_at
        const decodedToken = decodeJwtToken(tokens.access_token);
        console.log('Decoded JWT token:', decodedToken);
        
        // JWT expiry is in seconds, convert to milliseconds
        let jwtExpiry = null;
        if (decodedToken && decodedToken.exp) {
          jwtExpiry = decodedToken.exp * 1000; // Convert seconds to milliseconds
          console.log('JWT expiry time:', new Date(jwtExpiry).toISOString());
        }
        
        // Check for timezone in token
        if (decodedToken && decodedToken.tz) {
          console.log('Token timezone:', decodedToken.tz);
        }
        
        // Use JWT expiry if available, fall back to expires_at
        let expiryToCheck = jwtExpiry;
        if (!expiryToCheck) {
          console.log('JWT expiry not found, falling back to expires_at');
          expiryToCheck = parseExpiryTime(tokens.expires_at);
        }
        
        // Check token expiry
        const isExpired = !expiryToCheck || expiryToCheck <= Date.now();
        console.log('Is token expired based on JWT?', isExpired);
        
        if (isExpired) {
          console.log('Token is expired, attempting to refresh');
          const refreshed = await refreshToken();
          console.log('Token refresh result:', refreshed ? 'success' : 'failed');
          
          if (!refreshed) {
            console.log('Token refresh failed during initialization, clearing auth state');
            authService.removeToken();
            setUser(null);
            setProfile(null);
          } else {
            console.log('Successfully refreshed token during initialization');
          }
          
          setLoading(false);
          setInitialized(true);
          return;
        }
        
        // Case 2: Token is not expired, verify it's still valid
        console.log('Token not expired, verifying with server...');
        const response = await fetch(`${AUTH_CONFIG.apiUrl}/auth/verify`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokens.access_token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const responseStatus = response.status;
        console.log('Token verification response status:', responseStatus);
        
        if (response.ok) {
          console.log('Stored token is valid');
          
          // Set user state from stored tokens
          setUser({
            id: tokens.user_id,
            email: tokens.email
          });
          
          // Set profile if available in stored tokens
          if (tokens.profile) {
            setProfile(tokens.profile);
          }
          
          // Calculate time until expiry for logging
          if (expiryToCheck) {
            const timeUntilExpiry = Math.round((expiryToCheck - Date.now()) / 1000 / 60);
            console.log(`Token valid for approximately ${timeUntilExpiry} more minutes`);
            
            // Setup token refresh
            setupTokenRefresh(expiryToCheck);
          } else {
            console.warn('Could not determine token expiry, not scheduling refresh');
          }
        } else {
          console.log('Stored token is invalid (rejected by server), attempting to refresh');
          
          // Try to refresh token
          const refreshedTokens = await refreshToken();
          if (!refreshedTokens) {
            // If refresh failed, clear everything
            console.log('Token refresh failed after verification failure, signing out');
            authService.removeToken();
            setUser(null);
            setProfile(null);
          } else {
            console.log('Successfully refreshed token after verification failure');
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Clear everything on error
        authService.removeToken();
        setUser(null);
        setProfile(null);
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };
    
    initializeAuth();
    
    // Cleanup on unmount
    return () => {
      if (tokenRefreshTimeout.current) {
        clearTimeout(tokenRefreshTimeout.current);
      }
    };
  }, []);

  // Context value
  const value = {
    // Auth state
    user,
    profile,
    loading,
    initialized,
    isAuthenticated: !!user || authService.isAuthenticated(),
    authInitialized: initialized, // Added this specifically for ProtectedRoute
    
    // Auth functions
    signIn,
    signOut,
    requestEmailVerification,
    verifyEmail,
    checkEmailExists,
    updateUserProfile,
    refreshToken,
    refreshAuthState, // Added this for manual refresh
    
    // Timezone for token creation
    userTimezone
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use the auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}